import {
  VortexUserProfile,
  type VortexUserProfileUser,
} from "@vortexnyc/vortex-core/auth/react";
import { useMemo } from "react";
import { toast } from "sonner";

import { PageWrapper } from "~/components/page-wrapper";
import { userProfileClassNames } from "~/integrations/vortex-auth/class-names";
import { authClient } from "~/integrations/vortex-auth/client";
import { useCurrentUser } from "~/integrations/vortex-auth/hooks";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

// Vortex-auth account surface. Replaces the old hosted profile widget with
// the themeable `VortexUserProfile`, wiring its callbacks to the Better Auth
// client. Name/image edits + account deletion run against the client directly;
// password + 2FA flows surface a re-auth prompt (the interactive flows live in
// the shared vortex-auth runtime, not a plasma-local route).
export function AccountSettingsPage() {
  const { user, isLoaded } = useCurrentUser();

  const profileUser = useMemo<VortexUserProfileUser | null>(() => {
    if (!user) {
      return null;
    }
    return {
      id: user.id,
      email: user.primaryEmailAddress?.emailAddress ?? "",
      name: user.fullName ?? user.username ?? null,
    };
  }, [user]);

  return (
    <PageWrapper
      description="Manage your account and login settings"
      title="Account"
    >
      <VortexUserProfile
        classNames={userProfileClassNames}
        isLoading={!isLoaded}
        onChangePassword={() => {
          toast.info(
            "Password changes require re-authentication. Sign in again to update it."
          );
        }}
        onDeleteAccount={async () => {
          try {
            await authClient.deleteUser({});
            ProductEvents.settingsUpdated("account");
            toast.success("Account deletion requested.");
          } catch (error) {
            logger.exception("account_delete_failed", error);
            toast.error("Failed to delete account. Please try again.");
          }
        }}
        onManageTwoFactor={() => {
          toast.info("Two-factor authentication is managed during sign-in.");
        }}
        onUpdateProfile={async (input) => {
          try {
            await authClient.updateUser({
              name: input.name,
              ...(input.imageUrl != null ? { image: input.imageUrl } : {}),
            });
            ProductEvents.settingsUpdated("account");
            toast.success("Profile updated successfully");
          } catch (error) {
            logger.exception("account_profile_update_failed", error);
            toast.error("Failed to update profile. Please try again.");
          }
        }}
        user={profileUser}
      />
    </PageWrapper>
  );
}
